import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ClipboardPaste, Plus, Upload as UploadIcon, Zap } from "lucide-react";
import {
  buyersApi,
  type BuyerLeadDetailDto,
  type BuyerLeadListParams,
  type BuyerLeadStatus,
  type BuyerLeadSummaryDto,
  type BuyerNeedsActionReason,
} from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import { LogContactModal } from "@/components/buyers/LogContactModal";
import { ScheduleFollowUpModal } from "@/components/buyers/ScheduleFollowUpModal";
import { BuyerBulkActionsBar } from "@/components/buyers/BuyerBulkActionsBar";
import { SpecialistQuickPick } from "@/components/buyers/SpecialistQuickPick";
import { ListFilterBar } from "@/components/layout/ListFilterBar";
import { PageHeader } from "@/components/layout/PageHeader";
import { Modal } from "@/components/ui/Modal";
import { Select } from "@/components/ui/Select";
import {
  Button,
  ColumnChooser,
  EmptyState,
  ListToolbar,
  MissingValue,
  OverflowMenu,
  ScopeToggle,
  Skeleton,
  SortSelect,
  StatCard,
  StatsBar,
  StatusBadge,
  SubTable,
  SUB_TABLE_CHROME_THRESHOLD,
  TableValue,
  Timestamp,
  useToast,
  type ActiveFilterChip,
  type StatusTone,
  type SubTableColumn,
} from "@/design-system/components/ui";
import { calendarDaysUntil, formatCurrency } from "@/design-system/lib/format";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import { useBuyerTeams } from "@/hooks/buyers/useBuyerTeams";
import { useUserPreference } from "@/hooks/preferences/useUserPreference";
import { BUYERS_SCOPE_PREFERENCE, MY_SCOPE_PROBE, MyScopeEmptyState, useRememberedScope } from "./listScope";
import { useAuth } from "@/hooks/useAuth";
import { useListFilters } from "@/hooks/useListFilters";
import {
  anyFilterActive,
  BUYER_CHIP_KEYS,
  BUYER_FILTERS,
  BUYER_SORTS,
  BUYER_VIEWS,
  buyerLabel,
  defaultBuyerView,
  resolveSort,
  type BuyerCardFilter,
} from "./buyerFilters";
import { ArchiveBuyerModal } from "./archive/ArchiveBuyerModal";
import { pruneSelectionToVisible } from "./buyerSelection";
import { BuyerPager } from "./BuyerPager";
import { buyerStatusTone, verdictTone } from "./buyerListBits";
import { buyerPhaseLadder } from "./buyerPhases";
import { QuickAddBuyerModal } from "./QuickAddBuyerModal";
import { PasteToCreateBuyerModal } from "./PasteToCreateBuyerModal";
import { PresentBuyerWizard } from "./presentBuyer/PresentBuyerWizard";
import { QueryErrorState } from "./queryStates";
import { SendPreviewModal } from "./sendPreview/SendPreviewModal";
import { useSenderIdentity } from "./sendPreview/useSenderIdentity";

/** The server's page size. Also what tells `SubTable` it will never hold enough
 *  rows to earn chrome of its own — see the note at the table itself. */
const PAGE_SIZE = 25;

/** Per-user preference keys (Q7). */
const VIEW_PREFERENCE = "buyers-list-view";
const COLUMNS_PREFERENCE = "buyers-list-columns";

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * The buyers list, as the request the server actually accepts.
 *
 * <p>🔴 <c>sortBy</c>/<c>sortDirection</c> are on <c>PagedQuery</c> and are read
 * by <c>BuyerLeadService.ApplySort</c>, but the shared client mirror
 * (<c>BuyerLeadListParams</c>) never declared them, which is the only reason
 * these lists have had no sort control. They belong on that mirror; they are
 * declared here because <c>api/buyers.ts</c> is outside this change's scope, and
 * an intersection is honest about which fields are the shared contract and which
 * are the ones it is missing.</p>
 */
type BuyerListRequest = BuyerLeadListParams & {
  sortBy?: string;
  sortDirection?: "asc" | "desc";
};

/**
 * The statuses a stat card or a stage-bar segment stands for.
 *
 * <p>🔴 The `qualified` and `submitting` CARDS have been clickable since the
 * page shipped and filtered nothing — the token was parsed and then never
 * mapped to a request parameter. A control that cannot do what it appears to do
 * is worse than a read-only one, because it teaches the reader the surface is
 * lying. Both now filter on the status their own count is computed from.</p>
 */
function statusesForCardOrPhase(
  card: BuyerCardFilter,
  phase: string,
): BuyerLeadStatus[] | null {
  if (card === "qualified") return ["Qualified"];
  if (card === "submitting") return ["ActivelySubmitting"];
  if (!phase) return null;
  const rung = buyerPhaseLadder.phases.find((p) => p.key === phase);
  // An unrecognised rung filters nothing, so a stale bookmark degrades to the
  // unfiltered view rather than to an empty one.
  return rung ? [...rung.statuses] : null;
}

/** The needs-action reasons as the badge vocabulary (§6): amber for attention,
 *  red for overdue, muted for "there is something to do and nothing is wrong". */
function reasonBadge(
  reason: BuyerNeedsActionReason,
  noContactDays: number | undefined,
): { label: string; tone: StatusTone } {
  switch (reason) {
    case "FollowUpOverdue":
      return { label: "Follow-up overdue", tone: "danger" };
    case "FollowUpDueToday":
      return { label: "Follow-up today", tone: "warn" };
    case "NoContact":
      return { label: noContactLabel(noContactDays), tone: "warn" };
    case "NewAssignment":
      return { label: "New assignment", tone: "neutral" };
    case "QualifiedNoSubmission":
      return { label: "Qualified, no submission", tone: "neutral" };
    case "SubmissionAwaitingBuyer":
      return { label: "Awaiting buyer", tone: "neutral" };
    default:
      return { label: buyerLabel(reason), tone: "neutral" };
  }
}

/**
 * D10 — the no-contact window is the SERVER's (per team, then global) and the
 * client never hard-codes it. Until the stats payload arrives the label carries
 * no number at all, rather than a guess the next payload would contradict.
 */
function noContactLabel(noContactDays: number | undefined): string {
  return noContactDays == null ? "No contact" : `No contact ${noContactDays}d`;
}

/** The columns the reader may hide. The identity column is locked. */
const CHOOSABLE_COLUMNS = [
  { key: "buyer", label: "Buyer", locked: true },
  { key: "status", label: "Status" },
  { key: "verdict", label: "Qualification" },
  { key: "capacity", label: "Capacity" },
  { key: "submissions", label: "Submissions" },
  { key: "specialist", label: "Consultant" },
  { key: "lastContact", label: "Last contact" },
  { key: "nextAction", label: "Next action" },
];

/**
 * The buyers list — the specialist's home surface.
 *
 * Filters, page and the active stat card all live in the query string via the
 * shared `useListFilters` shell, so a view is bookmarkable and shareable and a
 * refresh lands where the specialist was. The reader's LAST VIEW and their
 * column choices ride per-user preferences (Q7), so a bare `/buyers` opens
 * where they left it on any device.
 */
export function BuyersListPage() {
  const filters = useListFilters(BUYER_FILTERS);
  const { values, page } = filters;
  const { hasPermission, hasRoleCode } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const canCreate = hasPermission("Buyers.Create");
  const canImport = hasPermission("Buyers.Import");
  const canPresent = hasPermission("Submissions.Manage");
  const canUpdate = hasPermission("Buyers.Update");
  const canManageAllTeams = hasPermission("Buyers.ManageAllTeams");
  // Page-scoped by construction: cleared whenever the page or the filters move,
  // so a selection can never outlive the rows it was made on.
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [quickOpen, setQuickOpen] = useState(false);
  const [pasteOpen, setPasteOpen] = useState(false);
  // The row a "⋯" menu action was opened on, and which action. Held here
  // rather than per row so there is ONE modal on the page — a modal per row
  // would mount one per result.
  const [rowAction, setRowAction] = useState<RowAction | null>(null);

  // Q7 — the last view and the hidden columns, per user.
  const viewPref = useUserPreference<string>(VIEW_PREFERENCE, "");
  const columnsPref = useUserPreference<string[]>(COLUMNS_PREFERENCE, []);
  const hiddenColumnKeys = useMemo(
    () => (Array.isArray(columnsPref.value) ? columnsPref.value.filter((k) => typeof k === "string") : []),
    [columnsPref.value],
  );

  // B5 — My by default, then the reader's last choice; a link's ?scope= wins.
  const scopeChoice = useRememberedScope(BUYERS_SCOPE_PREFERENCE, values.scope, (next) =>
    filters.set("scope", next),
  );

  // URL choice → persisted preference → the role's default. `view` is never
  // written to the URL at its default, so a bare `/buyers` resolves through
  // the preference and a shared `?view=` link opens the same rows for anyone.
  const activeView =
    values.view || (BUYER_VIEWS.some((v) => v.key === viewPref.value) ? viewPref.value : "") ||
    defaultBuyerView(hasRoleCode("F2"));
  const view = BUYER_VIEWS.find((v) => v.key === activeView) ?? BUYER_VIEWS[BUYER_VIEWS.length - 1];

  const selectView = (key: string) => {
    filters.set("view", key);
    viewPref.set(key);
  };

  // Both create modals write through `buyersApi` directly rather than through a
  // mutation hook, so the list and its stat cards are refreshed from here — one
  // prefix invalidation covers both.
  //
  // 🔴 The new buyer is OFFERED, not forced. A plain list host lets the
  // invalidation refresh underneath it; an unconditional navigate would throw
  // away the filtered view the specialist built, and would unmount Quick add
  // mid-batch on the very "Save & next" that exists to keep them typing. There
  // is no buyer record drawer to open, so the route rides the toast.
  const onCreated = (buyerId: string) => {
    void queryClient.invalidateQueries({ queryKey: buyerKeys.all });
    toast.success("Buyer created.", {
      action: { label: "Open buyer", onClick: () => navigate(`/buyers/${buyerId}`) },
    });
  };

  // "New this week" — anchored once per view selection so the query key does
  // not move on every render.
  const createdAfter = useMemo(
    () => (activeView === "new-this-week" ? new Date(Date.now() - WEEK_MS).toISOString() : undefined),
    [activeView],
  );

  // Effective sort: an explicit choice on the control wins; otherwise the view
  // says how it opens; otherwise the descriptor's default.
  const sortToken =
    values.sort !== BUYER_SORTS[0].value ? values.sort : (view.sort ?? BUYER_SORTS[0].value);

  // ONE params object feeds the list and the stats, so the cards can never
  // describe a different set of rows than the table below them. Everything but
  // the no-contact window lives here; that one term is folded in below, after
  // the stats have had a chance to say what the window is.
  const baseParams = useMemo<BuyerListRequest>(() => {
    const card = values.card;
    return {
      // The view's own params sit UNDER everything explicit.
      ...view.params,
      page,
      pageSize: PAGE_SIZE,
      ...resolveSort(BUYER_SORTS, sortToken),
      q: values.q || undefined,
      // The status dropdown wins over a stage-bar segment, which wins over the
      // view — each is more specific than the one after it.
      status: values.status
        ? [values.status]
        : (statusesForCardOrPhase(card, values.phase) ?? view.params.status ?? undefined),
      source: values.source ? [values.source] : undefined,
      overallVerdict: values.verdict ? [values.verdict] : undefined,
      mineOnly: scopeChoice.scope === "mine" ? true : undefined,
      buyerTeamId: canManageAllTeams && values.team ? values.team : undefined,
      unassigned: card === "unassigned" ? true : undefined,
      overdueFollowUpOnly: card === "overdue" ? true : undefined,
      dueTodayOnly: card === "due-today" ? true : undefined,
      convertedOnly: card === "converted" ? true : undefined,
      // One of the two attention pills; the other (no-contact) is added once
      // the window is known. Each rides the SAME server-side expression the
      // tile counted with, so the pill and this table cannot disagree.
      qualifiedNoSubmissionOnly:
        card === "qualified-no-submission" || view.params.qualifiedNoSubmissionOnly ? true : undefined,
      createdAfter,
      // Tri-state on the wire: undefined = the working list, true = archived
      // only. The stat tiles always describe the working book whichever view
      // this is showing, which the server enforces rather than this page.
      archived: values.archived === "only" ? true : undefined,
    };
  }, [page, values, view, sortToken, createdAfter, canManageAllTeams, scopeChoice.scope]);

  // The card counts describe the FILTERED set, minus the card filter itself —
  // otherwise clicking a card would collapse every other card to zero and there
  // would be no way back except the browser's Back button. The attention
  // predicates the VIEW adds are dropped for the same reason, so the view
  // chips' badges and the cards keep describing the whole book.
  const statsParams = useMemo<BuyerListRequest>(
    () => ({
      ...baseParams,
      page: 1,
      // Ordering cannot change a count. Dropped so choosing a sort does not
      // re-key the stats query and refetch every tile for nothing.
      sortBy: undefined,
      sortDirection: undefined,
      unassigned: undefined,
      overdueFollowUpOnly: undefined,
      convertedOnly: undefined,
      qualifiedNoSubmissionOnly: undefined,
      needsActionOnly: undefined,
      dueTodayOnly: undefined,
      createdAfter: undefined,
      status: values.status ? [values.status] : undefined,
    }),
    [baseParams, values.status],
  );
  const stats = useQuery({
    queryKey: buyerKeys.stats(statsParams),
    queryFn: () => buyersApi.stats(statsParams),
    enabled: !scopeChoice.isLoading,
  });

  // D5 #10 / D10 — the no-contact window is the SERVER's, so the pill's label,
  // its count and the rows it drills through to ride one number. Undefined
  // until the stats payload arrives; nothing on this page substitutes a guess.
  const noContactDays = stats.data?.noContactDays;
  const needsWindow = values.card === "no-contact" || activeView === "needs-contact";

  const params = useMemo<BuyerListRequest>(
    () => ({
      ...baseParams,
      noContactWithinDays: needsWindow ? noContactDays : undefined,
    }),
    [baseParams, needsWindow, noContactDays],
  );

  const list = useQuery({
    queryKey: buyerKeys.list(params),
    queryFn: () => buyersApi.list(params),
    // Wait for the persisted view: firing with the fallback and again with the
    // preference would be the double-fetch class, one render apart. The same
    // for the no-contact window — a list asked for "no contact" before the
    // stats have said how many days would fetch the WHOLE book under that card.
    enabled: !viewPref.isLoading && !scopeChoice.isLoading && !(needsWindow && stats.isPending),
  });

  // 🔴 Memoised on the query result rather than written inline. `?? []` in a
  // render body allocates a fresh array on every render for the whole time the
  // query is in flight — see `buyerSelection.ts` for what that cost once.
  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const total = list.data?.totalCount ?? 0;

  // B5 — whether an empty "My" view means the reader has no buyers at all,
  // asked only when it is empty.
  const myProbe = useQuery({
    queryKey: buyerKeys.list(MY_SCOPE_PROBE),
    queryFn: () => buyersApi.list(MY_SCOPE_PROBE),
    enabled: scopeChoice.scope === "mine" && list.isSuccess && rows.length === 0,
  });
  const names = useMemo(
    () => Object.fromEntries(rows.map((r) => [r.id, r.displayName])),
    [rows],
  );

  /**
   * §5 — progressive chrome, keyed on the WHOLE result set plus "has the reader
   * narrowed anything", never on the rows currently rendered.
   */
  const hasChrome = anyFilterActive(filters) || total >= SUB_TABLE_CHROME_THRESHOLD;

  const rowIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allSelected = rowIds.length > 0 && rowIds.every((id) => selectedIds.includes(id));

  // 🔴 The pruning MUST return `prev` unchanged when it drops nothing, or this
  // effect schedules a state change on every render and never settles. That is
  // `pruneSelectionToVisible`'s whole job — do not inline it back.
  useEffect(() => {
    setSelectedIds((prev) => pruneSelectionToVisible(prev, rowIds));
  }, [rowIds]);

  const buyerColumns = useMemo(
    () =>
      buildBuyerColumns({
        noContactDays,
        onAction: canUpdate || canPresent ? setRowAction : undefined,
        canPresent,
        canUpdate,
        selection: canUpdate
          ? {
              selectedIds,
              allSelected,
              onToggle: (id: string) =>
                setSelectedIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                ),
              onToggleAll: () => setSelectedIds(allSelected ? [] : rowIds),
            }
          : undefined,
      }),
    [noContactDays, canPresent, canUpdate, selectedIds, allSelected, rowIds],
  );

  // The stat cards: clicking the active card clears it, so a card is a toggle
  // rather than a one-way trip that needs the filter bar to undo.
  const toggleCard = (card: BuyerCardFilter) =>
    filters.set("card", values.card === card ? "" : card);
  const cardProps = (card: BuyerCardFilter) => ({
    onClick: () => toggleCard(card),
    active: values.card === card,
  });

  // Row 3 of the toolbar — everything narrowing the list that is not visible at
  // rest. Search and scope are on screen; these are behind "More filters".
  const activeChips = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];
    if (values.status)
      chips.push({ key: "status", label: `Status: ${buyerLabel(values.status)}`, onRemove: () => filters.set("status", "") });
    if (values.source)
      chips.push({ key: "source", label: `Source: ${buyerLabel(values.source)}`, onRemove: () => filters.set("source", "") });
    if (values.verdict)
      chips.push({ key: "verdict", label: `Qualification: ${buyerLabel(values.verdict)}`, onRemove: () => filters.set("verdict", "") });
    if (values.archived === "only")
      chips.push({ key: "archived", label: "Archived only", onRemove: () => filters.set("archived", "") });
    return chips;
  }, [values.status, values.source, values.verdict, values.archived, filters]);

  return (
    <>
      <PageHeader
        eyebrow="Buyer CRM"
        title="Buyers"
        description="Prospective business buyers, their qualification, and where each one is in the funnel."
        actions={
          <div className="flex items-center gap-space-2">
            {canImport && (
              <Link to="/buyers/import">
                <Button variant="secondary" size="sm">
                  <UploadIcon size={14} strokeWidth={1.5} aria-hidden /> Import CSV
                </Button>
              </Link>
            )}
            {canCreate && (
              <Button size="sm" variant="secondary" onClick={() => setQuickOpen(true)}>
                <Zap size={14} strokeWidth={1.5} aria-hidden /> Quick add
              </Button>
            )}
            {canCreate && (
              <Button size="sm" variant="secondary" onClick={() => setPasteOpen(true)}>
                <ClipboardPaste size={14} strokeWidth={1.5} aria-hidden /> Paste to create
              </Button>
            )}
            {canCreate && (
              <Link to="/buyers/new">
                <Button size="sm">
                  <Plus size={14} strokeWidth={1.5} aria-hidden /> New buyer
                </Button>
              </Link>
            )}
          </div>
        }
      />

      {quickOpen && (
        <QuickAddBuyerModal onClose={() => setQuickOpen(false)} onCreated={onCreated} />
      )}
      {pasteOpen && (
        <PasteToCreateBuyerModal onClose={() => setPasteOpen(false)} onCreated={onCreated} />
      )}
      {rowAction && <RowActionHost action={rowAction} onClose={() => setRowAction(null)} />}

      <StatsBar columns={6} data-testid="buyer-stats">
        <StatCard
          label="Total"
          value={stats.data?.total ?? "—"}
          onClick={() => filters.set("card", "")}
          active={values.card === ""}
        />
        <StatCard label="Unassigned" value={stats.data?.unassigned ?? "—"} {...cardProps("unassigned")} />
        <StatCard label="Qualified" value={stats.data?.qualified ?? "—"} {...cardProps("qualified")} />
        <StatCard label="Converted" value={stats.data?.converted ?? "—"} {...cardProps("converted")} />
        <StatCard label="Overdue follow-up" value={stats.data?.overdueFollowUp ?? "—"} {...cardProps("overdue")} />
        {/* Q5 — overdue (before today) and due today are two counts and two
            filters on the server; the card row says so too. */}
        <StatCard label="Due today" value={stats.data?.followUpDueToday ?? "—"} {...cardProps("due-today")} />
      </StatsBar>

      {/* §3 — one band: search | lens | sort; views | team | More filters |
          columns; the applied chips. Search and sort are §5-gated. */}
      <ListToolbar
        data-testid="buyers-toolbar"
        search={
          hasChrome
            ? {
                value: filters.inputs.q,
                onChange: (next) => filters.set("q", next),
                placeholder: "Search name, company, email, phone…",
                label: "Search buyers",
              }
            : undefined
        }
        lens={
          <ScopeToggle
            value={scopeChoice.scope}
            onChange={scopeChoice.choose}
            mineLabel="My buyers"
            allLabel="All buyers"
            label="Buyer view"
            data-testid="scope-toggle"
          />
        }
        sort={
          hasChrome ? (
            <SortSelect
              value={sortToken}
              onChange={(next) => filters.set("sort", next)}
              options={BUYER_SORTS}
              label="Sort buyers by"
            />
          ) : undefined
        }
        views={{
          items: BUYER_VIEWS.map((v) => ({
            key: v.key,
            label: v.key === "needs-contact" ? noContactLabel(noContactDays) : v.label,
            badge: v.key === "needs-action" ? stats.data?.needsAction : undefined,
          })),
          activeKey: activeView,
          onSelect: selectView,
          label: "Buyer views",
        }}
        primary={
          canManageAllTeams ? (
            <TeamSelect value={values.team} onChange={(next) => filters.set("team", next)} />
          ) : undefined
        }
        moreFilters={{
          activeCount: activeChips.length,
          content: (
            <ListFilterBar
              filters={filters}
              className="mb-0"
              rows={[
                {
                  keys: BUYER_CHIP_KEYS,
                  chipKeys: BUYER_CHIP_KEYS,
                  // Only status is counted server-side. A chip with no count
                  // renders bare rather than as a zero.
                  chipCounts: { status: stats.data?.byStatus },
                  className: "flex flex-col gap-space-2",
                },
                { keys: ["archived"] },
              ]}
            />
          ),
        }}
        activeChips={activeChips}
        onClearAllChips={() => {
          // ONE write — `set` builds its next params from THIS render's, so a
          // chain of sets would have the last overwrite the rest.
          filters.reset();
        }}
        actions={
          <ColumnChooser
            columns={CHOOSABLE_COLUMNS}
            hiddenKeys={hiddenColumnKeys}
            onChange={columnsPref.set}
            data-testid="buyer-columns"
          />
        }
      />

      {list.isLoading || viewPref.isLoading || scopeChoice.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : list.isError ? (
        <QueryErrorState
          what="the buyers list"
          onRetry={() => void list.refetch()}
          retrying={list.isFetching}
        />
      ) : rows.length === 0 && scopeChoice.scope === "mine" ? (
        <MyScopeEmptyState
          noun="buyers"
          nothingYet={myProbe.data?.totalCount === 0}
          onShowAll={() => scopeChoice.choose("all")}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No buyers match"
          description={
            canCreate
              ? "Widen the filters, or add a buyer to get started."
              : "Widen the filters to see more."
          }
        />
      ) : (
        /*
          🔴 `SubTable` with `chromeThreshold={PAGE_SIZE}`: its search, sort and
          footer act on the ARRAY IT HOLDS, which for a server-paged list is one
          page of 25, so at 25 the threshold can never be crossed and the PAGE
          owns all three controls, server-side, where they can tell the truth.
        */
        <>
          {canUpdate && (
            <BuyerBulkActionsBar
              selectedIds={selectedIds}
              names={names}
              onClear={() => setSelectedIds([])}
            />
          )}
          <SubTable<BuyerLeadSummaryDto>
            caption="Buyers"
            columns={buyerColumns}
            rows={rows}
            rowKey={(b) => b.id}
            rowSelected={(b) => selectedIds.includes(b.id)}
            chromeThreshold={PAGE_SIZE}
            hiddenColumnKeys={hiddenColumnKeys}
            stickyHeader
            data-testid="buyers-table"
          />
        </>
      )}

      <BuyerPager
        filters={filters}
        total={total}
        shown={rows.length}
        pageSize={PAGE_SIZE}
        hidden={!hasChrome}
      />
    </>
  );
}

/** The team narrowing, for a reader who dispatches every team. */
function TeamSelect({ value, onChange }: { value: string; onChange: (next: string) => void }) {
  const teams = useBuyerTeams();
  return (
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label="Team filter"
      className="h-10 w-auto min-w-[12rem]"
      data-testid="buyer-team-filter"
    >
      <option value="">All teams</option>
      {(teams.data ?? []).map((t) => (
        <option key={t.id} value={t.id}>
          {t.name}
        </option>
      ))}
    </Select>
  );
}

// ---------------------------------------------------------------------------
// Row actions (D8)
// ---------------------------------------------------------------------------

type RowActionKind = "contact" | "follow-up" | "education" | "present" | "reassign" | "archive";

interface RowAction {
  kind: RowActionKind;
  buyer: BuyerLeadSummaryDto;
}

/** ONE modal on the page, whichever row it was opened from. */
function RowActionHost({ action, onClose }: { action: RowAction; onClose: () => void }) {
  const { buyer } = action;
  switch (action.kind) {
    case "contact":
      return <LogContactModal buyerLeadId={buyer.id} buyerName={buyer.displayName} onClose={onClose} />;
    case "follow-up":
      return (
        <ScheduleFollowUpModal buyerLeadId={buyer.id} buyerName={buyer.displayName} onClose={onClose} />
      );
    case "education":
      return <RowEducationEmailModal buyer={buyer} onClose={onClose} />;
    case "present":
      return (
        <PresentBuyerWizard open onClose={onClose} buyerLeadId={buyer.id} buyerName={buyer.displayName} />
      );
    case "reassign":
      return (
        <Modal open onClose={onClose} title={`Reassign ${buyer.displayName}`} size="sm">
          <div className="grid gap-space-3">
            <p className="m-0 text-small text-gray-mid">
              Choose the consultant who takes this buyer. The move is recorded on the
              grant ledger with your reason.
            </p>
            <SpecialistQuickPick
              buyerLeadId={buyer.id}
              currentSpecialistUserId={buyer.assignedSpecialistUserId}
              currentSpecialistName={buyer.assignedSpecialistName}
              canReassign
              canUnassign={buyer.canUnassign === true}
              buyerDisplayName={buyer.displayName}
              onChanged={onClose}
            />
          </div>
        </Modal>
      );
    case "archive":
      // The modal reads id / displayName / status / submissionCount /
      // isArchived — every one of them is on the summary row.
      return (
        <ArchiveBuyerModal
          buyer={buyer as unknown as BuyerLeadDetailDto}
          mode={buyer.isArchived ? "unarchive" : "archive"}
          onClose={onClose}
        />
      );
    default:
      return null;
  }
}

/**
 * The education email from a list row — the same preview-and-confirm the
 * Qualification tab's card runs, over the same three endpoints.
 */
function RowEducationEmailModal({ buyer, onClose }: { buyer: BuyerLeadSummaryDto; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const identity = useSenderIdentity();
  const [subject, setSubject] = useState<string | null>(null);
  const [body, setBody] = useState<string | null>(null);

  const state = useQuery({
    queryKey: buyerKeys.educationEmail(buyer.id),
    queryFn: () => buyersApi.getEducationEmail(buyer.id),
  });
  const s = state.data;

  const send = useMutation({
    mutationFn: () =>
      buyersApi.sendEducationEmail(buyer.id, {
        subject: subject ?? undefined,
        body: body ?? undefined,
      }),
    onSuccess: () => {
      // The buyer prefix covers the row's last-contact stamp and the detail.
      qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success("Education email sent.");
      onClose();
    },
    onError: (e: unknown) => toast.error(extractErrorMessage(e)),
  });

  return (
    <SendPreviewModal
      title="Send the education email"
      onClose={onClose}
      toName={s?.buyerName ?? buyer.displayName}
      toEmail={s?.toEmail ?? buyer.email}
      audienceNote="It goes to the buyer. Nothing is sent to a listing broker or a seller."
      identity={identity}
      subject={subject ?? s?.subject}
      onSubjectChange={setSubject}
      body={body ?? s?.body}
      onBodyChange={setBody}
      bodyRows={14}
      attachments={
        s?.brochureFileName
          ? [{ name: s.brochureFileName, sizeBytes: s.brochureSizeBytes ?? null }]
          : undefined
      }
      confirmBlockedReason={s && !s.canSend ? s.blockedReason : undefined}
      renderPreview={({ subject: subj, body: bod }) =>
        buyersApi.previewOutreach(buyer.id, { subject: subj, body: bod }).then((r) => r.html)
      }
      replacesPreviewBody={
        state.isLoading ? <p className="text-small text-gray-mid">Loading the draft…</p> : undefined
      }
      footnote="Sending records this on the buyer and on their Communications tab; it does not change their status."
      confirmLabel="Send email"
      confirmTestId="education-confirm"
      onConfirm={() => send.mutate()}
      confirmDisabled={!s?.canSend || send.isPending || state.isLoading}
      pending={send.isPending}
    />
  );
}

// ---------------------------------------------------------------------------
// Columns (D7)
// ---------------------------------------------------------------------------

/** Page-scoped multi-select, supplied only to a reader who may act on rows. */
interface BuyerSelection {
  selectedIds: string[];
  allSelected: boolean;
  onToggle: (id: string) => void;
  onToggleAll: () => void;
}

interface ColumnOptions {
  /** Undefined until the stats payload states the window (D10). */
  noContactDays: number | undefined;
  /** Absent when this reader may take no row action — the menu is then not
   *  rendered at all, rather than rendering empty. */
  onAction?: (action: RowAction) => void;
  canPresent: boolean;
  canUpdate: boolean;
  selection?: BuyerSelection;
}

/** Overdue by the calendar (a wall-calendar due day against the operational
 *  day), never by clock arithmetic. */
function isOverdue(iso: string): boolean {
  return (calendarDaysUntil(iso) ?? 0) < 0;
}

/** Older than the no-contact window, or never contacted at all. With no
 *  window stated yet, nothing is tinted — a tint is a claim about a number. */
function isStale(lastContact: string | null | undefined, days: number | undefined): boolean {
  if (days == null) return false;
  const ago = calendarDaysUntil(lastContact);
  return ago == null || -ago > days;
}

/**
 * The row, as columns.
 *
 * 🔴 THE ROWS STAY RICH. A specialist triages their book from this table, so
 * the identity cell carries the contact line AND the needs-action badges (§6.3
 * — an exception sits inline after the name, never in a column that is empty
 * on every healthy row), and the last cell carries the row's own actions.
 */
function buildBuyerColumns({
  noContactDays,
  onAction,
  canPresent,
  canUpdate,
  selection,
}: ColumnOptions): SubTableColumn<BuyerLeadSummaryDto>[] {
  return [
    ...(selection
      ? [
          {
            key: "select",
            header: (
              <input
                type="checkbox"
                checked={selection.allSelected}
                onChange={selection.onToggleAll}
                aria-label="Select every buyer on this page"
                data-testid="bulk-select-all"
              />
            ),
            render: (buyer: BuyerLeadSummaryDto) => (
              <input
                type="checkbox"
                checked={selection.selectedIds.includes(buyer.id)}
                onChange={() => selection.onToggle(buyer.id)}
                aria-label={`Select ${buyer.displayName}`}
                data-testid={`bulk-select-${buyer.id}`}
              />
            ),
          } satisfies SubTableColumn<BuyerLeadSummaryDto>,
        ]
      : []),
    {
      key: "buyer",
      header: "Buyer",
      render: (buyer) => (
        <div className="min-w-0">
          <Link to={`/buyers/${buyer.id}`} className="text-gold-shadow hover:underline">
            {buyer.staffDisplayName ?? buyer.displayName}
          </Link>
          {/* Only ever set when the caller asked for archived rows. Muted
              prose, not a pill: the row is not in a state anyone acts on. */}
          {buyer.isArchived && (
            <span className="ml-space-2 text-caption text-gray-mid">Archived</span>
          )}
          <div className="text-caption text-gray-mid">
            {[
              buyer.email,
              buyer.city && buyer.state ? `${buyer.city}, ${buyer.state}` : buyer.state,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
          {(buyer.needsActionReasons?.length ?? 0) > 0 && (
            <div
              className="mt-space-1 flex flex-wrap gap-space-1"
              data-testid={`needs-action-${buyer.id}`}
            >
              {buyer.needsActionReasons!.map((reason) => {
                const badge = reasonBadge(reason, noContactDays);
                return (
                  <StatusBadge key={reason} tone={badge.tone}>
                    {badge.label}
                  </StatusBadge>
                );
              })}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (buyer) => (
        <>
          <StatusBadge tone={buyerStatusTone(buyer.status)}>
            {buyerLabel(buyer.status)}
          </StatusBadge>
          {/* The buyer's deal stage is DERIVED from the furthest-along
              submission (L5) — a caption, not a second status badge. */}
          {buyer.furthestSubmissionStatus && (
            <div className="text-caption text-gray-mid mt-space-1">
              Furthest: {buyerLabel(buyer.furthestSubmissionStatus)}
            </div>
          )}
        </>
      ),
    },
    {
      key: "verdict",
      header: "Qualification",
      render: (buyer) => (
        <StatusBadge tone={verdictTone(buyer.overallVerdict)}>
          {buyerLabel(buyer.overallVerdict)}
        </StatusBadge>
      ),
    },
    {
      key: "capacity",
      header: "Capacity",
      align: "right",
      render: (buyer) => (
        <>
          <div>
            <TableValue
              value={
                buyer.maxPurchasePrice != null ? formatCurrency(buyer.maxPurchasePrice) : null
              }
            />
          </div>
          {buyer.liquidCapital != null && (
            <div className="text-caption text-gray-mid">
              {formatCurrency(buyer.liquidCapital)} liquid
            </div>
          )}
        </>
      ),
    },
    {
      key: "submissions",
      header: "Submissions",
      align: "right",
      render: (buyer) => buyer.submissionCount,
    },
    {
      key: "specialist",
      header: "Consultant",
      // A CONTROL on exactly the rows the server says this caller may re-home
      // (`canReassign`), plain text on every other — never a select that 400s.
      render: (buyer) => (
        <SpecialistQuickPick
          buyerLeadId={buyer.id}
          currentSpecialistUserId={buyer.assignedSpecialistUserId}
          currentSpecialistName={buyer.assignedSpecialistName}
          canReassign={buyer.canReassign === true}
          canUnassign={buyer.canUnassign === true}
          buyerDisplayName={buyer.displayName}
          size="sm"
        />
      ),
    },
    {
      key: "lastContact",
      header: "Last contact",
      // Tinted once it is older than the no-contact window the stats state —
      // the same number the "No contact" badge and view use.
      render: (buyer) =>
        buyer.lastContactAtUtc ? (
          <Timestamp
            value={buyer.lastContactAtUtc}
            className={isStale(buyer.lastContactAtUtc, noContactDays) ? "text-status-warning-ink" : undefined}
          />
        ) : (
          <span className="italic text-gray-mid">Never</span>
        ),
    },
    {
      key: "nextAction",
      header: "Next action",
      // §7 — a plain missing value is `—` with no action here: the door to
      // schedule one is the row menu, where the other row verbs already are.
      render: (buyer) =>
        buyer.nextActionDueAt ? (
          <div className="min-w-0">
            <Timestamp
              value={buyer.nextActionDueAt}
              className={isOverdue(buyer.nextActionDueAt) ? "text-status-error" : undefined}
            />
            {buyer.nextActionTitle && (
              <div className="truncate text-caption text-gray-mid">{buyer.nextActionTitle}</div>
            )}
          </div>
        ) : (
          <MissingValue />
        ),
    },
    {
      key: "actions",
      header: "Actions",
      className: "w-[1%]",
      // Both halves of the server's rule, neither re-derived: the reader's
      // permissions (captured above) and the row's own server-resolved
      // `canEdit` / `canReassign`. No offerable item → no menu at all, because
      // a "⋯" that opens onto nothing is worse than no "⋯".
      render: (buyer) => {
        if (!onAction) return null;
        const items = [];
        if (canUpdate && buyer.canEdit) {
          items.push({ label: "Log contact", onSelect: () => onAction({ kind: "contact", buyer }) });
          items.push({ label: "Schedule follow-up", onSelect: () => onAction({ kind: "follow-up", buyer }) });
          items.push({ label: "Send education email", onSelect: () => onAction({ kind: "education", buyer }) });
        }
        if (canPresent && buyer.canEdit) {
          items.push({ label: "Present to a listing", onSelect: () => onAction({ kind: "present", buyer }) });
        }
        if (buyer.canReassign === true) {
          items.push({ label: "Reassign", onSelect: () => onAction({ kind: "reassign", buyer }) });
        }
        if (canUpdate && buyer.canEdit) {
          items.push({
            label: buyer.isArchived ? "Unarchive" : "Archive",
            onSelect: () => onAction({ kind: "archive", buyer }),
          });
        }
        return items.length > 0 ? (
          <OverflowMenu
            label={`Actions for ${buyer.displayName}`}
            data-testid="buyer-row-actions"
            items={items}
          />
        ) : null;
      },
    },
  ];
}
